import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import { ClickAwayListener, styled } from '@mui/material';

import { useClientHasModule } from 'hooks/client';
import { BUDGET } from 'business/constants';
import {
    useUserCreationFormContext,
    useUserCreationFormDispatchContext,
} from './CreateUserContext/CreateUserFormContext';
import { useValidateUserPersonalInformation } from './hooks/useValidateUserForm';

import EditUserModalTopBar from './components/ui/EditUserModalTopBar';
import EditUserModalContent from './components/EditUserModalContent';
import { useUpdateUserAndUserRights } from './hooks/useUpdateUser';

const INFORMATION_SECTION_PATH =
    'adminSpace.editUser.modal.sideBar.generalSection.informationSection';

const StyBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '90vh',
    overflow: 'hidden',
}));

const StyDivider = styled(Divider)(({ theme }) => ({
    lineHeight: theme.spacing(0.25),
    '& .MuiDivider-root::before': {
        content: 'none',
    },
}));

type Props = {
    isModalOpen: boolean;
    otherUsersEmails: string[];
    setIsModalOpen: (arg: boolean) => void;
    userId: number;
};

const EditUserModal = ({
    isModalOpen,
    otherUsersEmails,
    setIsModalOpen,
    userId,
}: Props): JSX.Element => {
    const { t } = useTranslation();

    const [activeSectionTitle, setActiveSectionTitle] = useState<string>(
        t(INFORMATION_SECTION_PATH),
    );

    const userCreationForm = useUserCreationFormContext();
    const { hasDispatchBeenCalled } = useUserCreationFormDispatchContext();

    const invalidPersonalInformationMessage =
        useValidateUserPersonalInformation(userCreationForm, otherUsersEmails);

    const hasBudgetModule = useClientHasModule(BUDGET);

    const updateUserMutation = useUpdateUserAndUserRights(
        userId,
        userCreationForm,
        hasBudgetModule,
    );

    const handleCloseModal = () => {
        if (!hasDispatchBeenCalled.current) {
            setIsModalOpen(false);
            return;
        }
        if (window.confirm(t('adminSpace.editUser.leavingModalConfirmation'))) {
            setIsModalOpen(false);
        }
    };

    const onUpdateUserAction = (): void => {
        if (invalidPersonalInformationMessage) {
            setActiveSectionTitle(t(INFORMATION_SECTION_PATH));
            return;
        }
        updateUserMutation.mutate();
        setIsModalOpen(false);
    };

    return (
        <Modal open={isModalOpen}>
            <ClickAwayListener onClickAway={handleCloseModal}>
                <StyBox>
                    <EditUserModalTopBar
                        handleCloseModal={handleCloseModal}
                        onSaveClick={() => onUpdateUserAction()}
                    />
                    <StyDivider aria-hidden="true" />
                    <EditUserModalContent
                        activeSectionTitle={activeSectionTitle}
                        otherUsersEmails={otherUsersEmails}
                        setActiveSectionTitle={setActiveSectionTitle}
                        userId={userId}
                    />
                </StyBox>
            </ClickAwayListener>
        </Modal>
    );
};

export default EditUserModal;
